import { cn } from "@promptwatch/ui/lib/utils";
import { ChevronDown } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import type { Heading } from "@/lib/markdown";

interface TableOfContentsProps {
  headings: Heading[];
}

const TableOfContents = ({ headings }: TableOfContentsProps) => {
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(true); // Collapsed by default on mobile
  const observer = useRef<IntersectionObserver | null>(null);
  const tocRef = useRef<HTMLDivElement>(null);

  // Calculate the active heading index for the scrollbar indicator
  const activeIndex = activeSlug ? headings.findIndex((heading) => heading.slug === activeSlug) : -1;
  const indicatorHeight = Math.max((100 / headings.length) * 0.6, 4); // Smaller indicator, minimum 4% height
  const maxPosition = 100 - indicatorHeight; // Maximum position to prevent overflow
  const indicatorPosition = activeIndex >= 0 ? Math.min((activeIndex / Math.max(headings.length - 1, 1)) * maxPosition, maxPosition) : 0;

  useEffect(() => {
    // Disconnect previous observer if it exists
    if (observer.current) {
      observer.current.disconnect();
    }

    // Create a new Intersection Observer
    observer.current = new IntersectionObserver(
      (entries) => {
        // Find the first entry that is intersecting or the topmost visible entry
        let intersectingEntry = entries.find((entry) => entry.isIntersecting);

        if (!intersectingEntry) {
          // If no entry is intersecting, find the topmost visible one above the viewport center
          const visibleEntries = entries.filter((entry) => entry.boundingClientRect.top < window.innerHeight / 2);
          if (visibleEntries.length > 0) {
            // Sort by bottom position descending (closest to center from top)
            visibleEntries.sort((a, b) => b.boundingClientRect.bottom - a.boundingClientRect.bottom);
            intersectingEntry = visibleEntries[0];
          }
        }

        if (intersectingEntry) {
          setActiveSlug(intersectingEntry.target.id);
        } else if (window.scrollY === 0) {
          setActiveSlug(null); // No active slug if scrolled to top
        }
      },
      {
        // Observe elements crossing the horizontal center of the viewport
        rootMargin: "0px 0px -50% 0px", // Adjust rootMargin to trigger based on center
        threshold: 0, // Trigger as soon as any part is visible within the rootMargin bounds
      },
    );

    const elements = headings.map(({ slug }) => document.getElementById(slug));

    // Use for...of loop and check for element existence
    for (const el of elements) {
      if (el) {
        observer.current?.observe(el);
      }
    }

    // Store the elements to unobserve later
    const observedElements = elements.filter((el): el is HTMLElement => el !== null);

    return () => {
      if (observer.current) {
        // Use for...of loop for cleanup
        for (const el of observedElements) {
          observer.current?.unobserve(el);
        }
      }
      // Disconnect observer after unobserving all elements
      observer.current?.disconnect();
    };
  }, [headings]); // Rerun effect if headings change

  if (!headings || headings.length === 0) {
    return null; // Don't render ToC if there are no headings
  }

  return (
    <div ref={tocRef} className="">
      {/* Mobile collapse/expand header - entire area is clickable on mobile */}
      <button
        className="flex items-center justify-between lg:block cursor-pointer lg:cursor-default  lg:hover:bg-transparent rounded-md p-1 -m-1 lg:p-0 lg:m-0 transition-colors"
        onClick={() => setIsCollapsed(!isCollapsed)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsCollapsed(!isCollapsed);
          }
        }}
        aria-label={isCollapsed ? "Expand table of contents" : "Collapse table of contents"}
      >
        <h3 className="text-sm font-semibold mb-3 text-gray-700">Table of Contents</h3>
        {/* Mobile collapse/expand icon - only visible on mobile */}
        <div className="lg:hidden flex items-center gap-1 pl-2 text-sm text-gray-600 transition-colors mb-3">
          <ChevronDown className={cn("size-4 transition-transform duration-200", { "rotate-180": !isCollapsed })} />
        </div>
      </button>

      {/* Table of contents with scrollbar indicator */}
      <div
        className={cn(
          "relative transition-all duration-200 overflow-hidden",
          "lg:block", // Always visible on desktop
          { hidden: isCollapsed }, // Hidden when collapsed on mobile
          { block: !isCollapsed }, // Visible when expanded on mobile
        )}
      >
        {/* Scrollbar track */}
        <div className="absolute left-0 top-0 w-1 h-full bg-gray-200 rounded-full">
          {/* Scrollbar indicator */}
          <div
            className="absolute w-1 bg-primary rounded-full transition-all duration-700 ease-in-out will-change-transform"
            style={{
              top: `${indicatorPosition}%`,
              height: `${indicatorHeight}%`,
            }}
          />
        </div>

        {/* Table of contents list */}
        <ul className="space-y-2 text-sm pl-4">
          {headings.map((heading) => (
            <li
              key={heading.slug}
              className={cn(
                "text-gray-500 hover:text-gray-900 line-clamp-1",
                { "pl-3": heading.level === 3 }, // Indent H3 level
              )}
            >
              <Link
                href={`#${heading.slug}`}
                shallow
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById(heading.slug);
                  if (element) {
                    const offsetTop = element.getBoundingClientRect().top + window.scrollY - 80; // Apply 40px offset
                    window.scrollTo({
                      top: offsetTop,
                      behavior: "smooth",
                    });
                  }

                  setActiveSlug(heading.slug); // Immediately set active slug on click
                }}
              >
                {heading.text}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default TableOfContents;
